<template>
  <el-dialog
    :title="`从 ${companyName} 标签池选用标签`"
    :visible.sync="visible"
    width="860px"
    :close-on-click-modal="false"
    top="5vh"
  >
    <el-alert
      type="info"
      :closable="false"
      show-icon
      style="margin-bottom:16px"
    >
      当前业务池：<b>{{ bizType }}</b>，可从公司标签池选择并调整标签顺序。
    </el-alert>

    <div class="pick-layout">
      <div class="pick-layout__left">
        <div class="pick-search">
          <el-input
            v-model="searchKeyword"
            placeholder="搜索公司标签..."
            prefix-icon="el-icon-search"
            size="small"
            clearable
          ></el-input>
          <el-button
            size="small"
            :type="allFilteredSelected ? 'primary' : 'default'"
            :disabled="filteredTags.length === 0"
            @click="toggleSelectAll"
          >{{ allFilteredSelected ? '取消全选' : '全选' }}</el-button>
        </div>
        <div class="pick-list custom-scrollbar">
          <div
            v-for="tag in filteredAvailableTags"
            :key="tag.detailId"
            class="pick-item"
            :class="{ 'pick-item--selected': isSelected(tag.detailId) }"
            @click="toggleTag(tag)"
          >
            <el-checkbox :value="isSelected(tag.detailId)" class="pick-item__checkbox"></el-checkbox>
            <div class="pick-item__info">
              <div class="pick-item__name">{{ tag.name }}
                <span class="pick-item__code">{{ tag.code }}</span>
              </div>
              <div class="pick-item__meta">{{ tag.desc || '' }}</div>
            </div>
          </div>
        </div>
      </div>
      <div class="pick-layout__right">
        <h4 class="pick-summary__title">
          已选汇总（共 {{ selectedTags.length }} 个，
          已配置 {{ configuredCount }} 个，
          新选 {{ newCount }} 个）
        </h4>
        <div class="pick-list pick-list--selected custom-scrollbar">
          <div v-if="selectedTags.length === 0" class="pick-empty">
            <i class="el-icon-tag" style="font-size:40px;color:#e8e8e8"></i>
            <p>暂未选择标签</p>
          </div>
          <div
            v-for="(tag, idx) in selectedTags"
            :key="tag.detailId"
            class="pick-selected-item"
          >
            <div class="pick-selected-item__header">
              <span class="pick-selected-item__rank">#{{ idx + 1 }}</span>
              <span class="pick-selected-item__name">{{ tag.name }}</span>
              <el-tag v-if="tag.isConfigured" type="warning" size="mini">已配置</el-tag>
              <el-button
                v-else
                icon="el-icon-close"
                size="mini"
                circle
                @click="removeTag(tag.detailId)"
              ></el-button>
            </div>
            <div class="pick-selected-item__controls">
              <el-button size="mini" @click="moveTag(idx, 'top')">置顶</el-button>
              <el-button size="mini" @click="moveTag(idx, 'bottom')">置底</el-button>
              <span class="position-input">
                位置
                <input
                  type="number"
                  :value="idx + 1"
                  @change="onPositionChange(idx, $event)"
                />
                / {{ selectedTags.length }}
              </span>
            </div>
          </div>
        </div>
        <div class="pick-actions">
          <el-button type="primary" style="flex:1" @click="onConfirm">确认选择</el-button>
          <el-button style="flex:1" @click="visible = false">取消</el-button>
        </div>
      </div>
    </div>
  </el-dialog>
</template>

<script>
export default {
  name: 'PickCompanyDialog',
  props: {
    companyTags: { type: Array, default: () => [] },
    companyName: { type: String, default: '' },
    bizType: { type: String, default: '' },
    configuredIds: { type: Array, default: () => [] }
  },
  data() {
    return {
      visible: false,
      searchKeyword: '',
      selectedTags: []
    };
  },
  computed: {
    availableTags() {
      return this.companyTags.filter(
        t => !this.selectedTags.some(s => s.detailId === t.detailId)
      );
    },
    // 按关键字过滤后的全部公司标签（含已选中的）
    filteredTags() {
      if (!this.searchKeyword) return this.companyTags;
      const kw = this.searchKeyword.toLowerCase();
      return this.companyTags.filter(t =>
        `${t.name}${t.code}${t.desc || ''}`.toLowerCase().includes(kw)
      );
    },
    filteredAvailableTags() {
      return this.filteredTags.filter(
        t => !this.selectedTags.some(s => s.detailId === t.detailId)
      );
    },
    // 当前筛选结果是否已全部选中
    allFilteredSelected() {
      if (!this.filteredTags.length) return false;
      return this.filteredTags.every(t => this.isSelected(t.detailId));
    },
    configuredCount() {
      return this.selectedTags.filter(t => t.isConfigured).length;
    },
    newCount() {
      return this.selectedTags.filter(t => !t.isConfigured).length;
    }
  },
  methods: {
    // tags: 可选，传入父级刚获取到的最新公司标签，避免依赖 props 的异步更新时机
    open(tags) {
      const all = Array.isArray(tags) ? tags : this.companyTags;
      const configured = this.configuredIds
        .map(detailId => all.find(t => t.detailId === detailId))
        .filter(Boolean)
        .map(t => ({ ...t, isConfigured: true }));
      this.selectedTags = configured;
      this.searchKeyword = '';
      this.visible = true;
    },
    isSelected(detailId) {
      return this.selectedTags.some(t => t.detailId === detailId);
    },
    // 全选/取消全选：只作用于当前搜索关键字过滤出的标签，已配置的标签不可移除
    toggleSelectAll() {
      const list = this.filteredTags;
      if (!list.length) return;
      if (this.allFilteredSelected) {
        const ids = list.map(t => t.detailId);
        this.selectedTags = this.selectedTags.filter(
          t => ids.indexOf(t.detailId) === -1 || t.isConfigured
        );
        return;
      }
      const next = this.selectedTags.slice();
      list.forEach((tag) => {
        if (next.some(s => s.detailId === tag.detailId)) return;
        next.push({ ...tag, isConfigured: false });
      });
      this.selectedTags = next;
    },
    toggleTag(tag) {
      const idx = this.selectedTags.findIndex(t => t.detailId === tag.detailId);
      if (idx > -1) {
        this.selectedTags.splice(idx, 1);
      } else {
        this.selectedTags.push({ ...tag, isConfigured: false });
      }
    },
    removeTag(detailId) {
      const idx = this.selectedTags.findIndex(t => t.detailId === detailId);
      if (idx > -1 && !this.selectedTags[idx].isConfigured) {
        this.selectedTags.splice(idx, 1);
      }
    },
    moveTag(from, direction) {
      if (this.selectedTags.length <= 1) return;
      const item = this.selectedTags.splice(from, 1)[0];
      if (direction === 'top') {
        this.selectedTags.unshift(item);
      } else {
        this.selectedTags.push(item);
      }
    },
    moveTagTo(from, value) {
      let target = parseInt(value, 10);
      if (isNaN(target) || target < 1) target = 1;
      if (target > this.selectedTags.length) target = this.selectedTags.length;
      if (target === from + 1) return;
      const item = this.selectedTags.splice(from, 1)[0];
      this.selectedTags.splice(target - 1, 0, item);
    },
    // 单步上移/下移：step 为 -1 上移、1 下移
    moveTagStep(index, step) {
      const target = index + step;
      if (target < 0 || target >= this.selectedTags.length) return;
      const [item] = this.selectedTags.splice(index, 1);
      this.selectedTags.splice(target, 0, item);
    },
    // 原生数字框的小箭头是"数值 ±1"（+1 序号变大＝往下），与"上移/下移"直觉相反，
    // 这里做方向反转：↑ 上移一位、↓ 下移一位；直接输入其它数字则跳到对应位置
    onPositionChange(index, event) {
      const total = this.selectedTags.length;
      const current = index + 1;
      const value = parseInt(event.target.value, 10);
      // 浏览器已改写了输入框的值，先复位，避免数据未变化时显示错乱
      event.target.value = current;
      if (isNaN(value)) return;
      const diff = value - current;
      // 先按 ±1 判定单步（此时越界的 0、total+1 仍可作上下移动），再处理后者的跳转
      if (diff === 1 || diff === -1) {
        this.moveTagStep(index, -diff);
        return;
      }
      if (diff === 0) return;
      let target = value;
      if (target < 1) target = 1;
      if (target > total) target = total;
      this.moveTagTo(index, target);
    },
    onConfirm() {
      // 提交
      // 刷新业务标签池
      this.$emit('confirm', [...this.selectedTags]);
      this.visible = false;
    }
  }
};
</script>

<style lang="less" scoped>
.pick-layout {
  display: flex;
  gap: 20px;
  height: 460px;
  overflow: hidden;

  &__left {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  &__right {
    width: 340px;
    background: #fafafa;
    border-radius: 8px;
    padding: 16px;
    display: flex;
    flex-direction: column;
  }
}

.pick-search {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;

  .el-input {
    flex: 1;
    min-width: 0;
  }

  .el-button {
    flex-shrink: 0;
  }
}

.pick-list {
  flex: 1;
  overflow-y: auto;

  &--selected {
    border: 1px solid #e8e8e8;
    background: #fff;
    border-radius: 8px;
    padding: 8px;
  }
}

.pick-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  cursor: pointer;
  margin-bottom: 8px;
  transition: all 0.2s;

  &:hover {
    border-color: #a855f7;
  }

  &--selected {
    border-color: #a855f7;
    background: #faf5ff;
  }

  &__checkbox { pointer-events: none; }

  &__info {
    flex: 1;
    min-width: 0;
  }

  &__name {
    font-weight: bold;
    color: #303133;
    font-size: 13px;
  }

  &__code {
    font-size: 11px;
    color: #c0c4cc;
    font-family: monospace;
    margin-left: 6px;
  }

  &__meta {
    font-size: 11px;
    color: #c0c4cc;
  }
}

.pick-summary__title {
  font-size: 13px;
  font-weight: bold;
  color: #303133;
  margin: 0 0 12px;
}

.pick-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #c0c4cc;
  font-size: 12px;
}

.pick-selected-item {
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 10px;
  margin-bottom: 8px;

  &__header {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 6px;
  }

  &__rank {
    font-size: 11px;
    font-weight: bold;
    color: #909399;
    width: 24px;
  }

  &__name {
    font-weight: bold;
    font-size: 13px;
    color: #303133;
    flex: 1;
  }

  &__controls {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    color: #909399;

    input {
      width: 56px;
      text-align: center;
      border: 1px solid #e8e8e8;
      border-radius: 4px;
      padding: 2px;
    }
  }
}

.position-input {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: auto;
}

.pick-actions {
  display: flex;
  gap: 8px;
  margin-top: 12px;
}

.custom-scrollbar::-webkit-scrollbar { width: 6px; }
.custom-scrollbar::-webkit-scrollbar-thumb { background-color: #e8e8e8; border-radius: 10px; }
</style>



// WEBPACK FOOTER //
// src/page/main/tagpools/pick-company-dialog.vue